import isNil from 'lodash/isNil'
import type { BillingProfileRecord } from '../../api/dto/client-billing-profile.dto'
import type { ParsedRow } from './bulkUpload.helpers'
import { BillingProfileStatus, isValidDueDay, PaymentType } from './types'

/**
 * What a row will do once uploaded. The upload writes a cell only when it is
 * filled in and differs from what is stored, so a row can name a company and
 * still change nothing.
 */
export type RowPlanKind = 'create' | 'update' | 'unchanged' | 'needsEmails'

export interface RowPlan {
  kind: RowPlanKind
  /** Columns this row will change, for the Update tooltip. */
  changedFields: string[]
}

export interface PlanSummary {
  create: number
  update: number
  unchanged: number
  needsEmails: number
}

/** Blank cells and rejected values are left to the profile, so neither counts. */
const PAYMENT_TYPES: Record<string, PaymentType> = {
  wire: PaymentType.WIRE,
  stripe: PaymentType.STRIPE,
}

const profileKey = (clientName: string): string =>
  clientName.trim().toLowerCase()

/** To Emails is one cell holding several addresses, separated by ; or , */
const splitEmails = (value: string): string[] =>
  value
    .split(/[;,]/)
    .map((email) => email.trim())
    .filter(Boolean)

/** Order in the cell is not a change, so compare the addresses as a set. */
const sameEmails = (stored: string[], incoming: string[]): boolean => {
  if (stored.length !== incoming.length) return false
  const sortedStored = [...stored].sort()
  const sortedIncoming = [...incoming].sort()
  return sortedStored.every((email, i) => email === sortedIncoming[i])
}

const toPaymentType = (value: string): PaymentType | null =>
  PAYMENT_TYPES[value.trim().toLowerCase()] ?? null

const toDueDay = (value: string): number | null =>
  isValidDueDay(value) ? Number(value.trim()) : null

const isPreferredOver = (
  candidate: BillingProfileRecord,
  current: BillingProfileRecord
): boolean => {
  const candidateIsActive = candidate.status === BillingProfileStatus.Active
  const currentIsActive = current.status === BillingProfileStatus.Active
  if (candidateIsActive !== currentIsActive) return candidateIsActive
  return candidate.createdAt > current.createdAt
}

/**
 * The sheet names a company, and the upload lands on the profile that company
 * already has, deactivated ones included. Newest active wins when a company
 * somehow has several.
 */
export const indexProfilesByClientName = (
  profiles: BillingProfileRecord[]
): Map<string, BillingProfileRecord> => {
  const byName = new Map<string, BillingProfileRecord>()
  for (const profile of profiles) {
    const key = profileKey(profile.clientName)
    const current = byName.get(key)
    if (isNil(current) || isPreferredOver(profile, current)) {
      byName.set(key, profile)
    }
  }
  return byName
}

export const planRow = (
  row: ParsedRow,
  profile: BillingProfileRecord | undefined
): RowPlan => {
  const emails = splitEmails(row.toEmails)

  if (isNil(profile)) {
    // A profile cannot be created without recipients, so such a row is rejected.
    return {
      kind: emails.length > 0 ? 'create' : 'needsEmails',
      changedFields: [],
    }
  }

  const changedFields: string[] = []
  if (emails.length > 0 && !sameEmails(profile.toEmails, emails)) {
    changedFields.push('To Emails')
  }
  const paymentType = toPaymentType(row.paymentType)
  if (!isNil(paymentType) && profile.paymentType !== paymentType) {
    changedFields.push('Payment Type')
  }
  const dueDay = toDueDay(row.dueDay)
  if (!isNil(dueDay) && profile.dueDay !== dueDay) {
    changedFields.push('Due Day')
  }

  return {
    kind: changedFields.length === 0 ? 'unchanged' : 'update',
    changedFields,
  }
}

export const summarizePlans = (plans: readonly RowPlan[]): PlanSummary => {
  const summary: PlanSummary = {
    create: 0,
    update: 0,
    unchanged: 0,
    needsEmails: 0,
  }
  plans.forEach((plan) => {
    summary[plan.kind]++
  })
  return summary
}

/** One line for the preview header, naming only the buckets that have rows. */
export const describePlanSummary = (summary: PlanSummary): string => {
  const parts: string[] = []
  if (summary.create > 0) parts.push(`${summary.create} new`)
  if (summary.update > 0) parts.push(`${summary.update} to update`)
  if (summary.unchanged > 0) parts.push(`${summary.unchanged} unchanged`)
  if (summary.needsEmails > 0) {
    parts.push(`${summary.needsEmails} missing recipients`)
  }
  return parts.join(', ')
}
